import { useMemo } from 'react'
import type { NewShapeTextSeedStyle } from '@/lib/shapes/types/new-shape-text-seed-style'
import { useFontsOptional } from '@/providers/font'
import type { CenterCanvasConfig } from '../types/center-canvas-config'

/**
 * The type style a brand-new text frame starts with: the book/checkpoint default font and size
 * resolved to real ids, plus the last-used TypePanel kerning/tracking/underline/alignment. The
 * font and size never follow the TypePanel, so restyling one frame leaves new frames on the
 * default.
 */
export function useDefaultFontSeed({
  defaultFontFamilyId,
  defaultFontName,
  defaultFontSize,
  newTextBoxKerning,
  newTextBoxTracking,
  newTextBoxUnderline,
  newTextBoxAlignment,
}: Pick<
  CenterCanvasConfig,
  | 'defaultFontFamilyId'
  | 'defaultFontName'
  | 'defaultFontSize'
  | 'newTextBoxKerning'
  | 'newTextBoxTracking'
  | 'newTextBoxUnderline'
  | 'newTextBoxAlignment'
>) {
  // Resolve the book/checkpoint default font family to an actual fontId, the same id-then-name
  // precedence the Type panel's `resolvedDefaultFontFamilyId`
  // (`type-panel/hooks/use-default-font-family.ts`) uses — so a brand-new text frame
  // (drag-created or a shape's first turn into text) seeds from the configured Planbook default
  // rather than the hardcoded `DEFAULT_FONT_FAMILY_ID`/`DEFAULT_FONT_ID` when no TypePanel style
  // has been set yet this session (WMPCM-9239 follow-up: default font/size not applied to new text).
  // Optional: this component renders in tests without a `<FontProvider>` ancestor.
  const fontsCtx = useFontsOptional()

  const resolvedDefaultFontFamilyId = useMemo(() => {
    if (defaultFontFamilyId != null) return defaultFontFamilyId

    if (defaultFontName) {
      // `getFamilyIdByCssName` normalizes (trim/collapse-whitespace/lowercase) and checks both
      // `displayName` and `familyName` — an exact-match `.find(f => f.familyName === defaultFontName)`
      // silently fails whenever the checkpoint's NSD `DefaultFont` string differs from the API's
      // `familyName` spelling/casing (e.g. "Times New Roman" vs. the CSS-registration form), which
      // is exactly the class of mismatch that fell through to the hardcoded Helvetica fallback below.
      const byName = fontsCtx?.getFamilyIdByCssName(defaultFontName)

      if (byName != null) return byName
    }

    return undefined
  }, [defaultFontFamilyId, defaultFontName, fontsCtx])

  const resolvedDefaultFontId = useMemo(
    () =>
      resolvedDefaultFontFamilyId != null
        ? fontsCtx?.pickDefaultFontIdForFamilyId(resolvedDefaultFontFamilyId)
        : undefined,
    [resolvedDefaultFontFamilyId, fontsCtx]
  )

  /**
   * Type style a shape gets the first time it is turned into a text frame — the same style the
   * text tool seeds a new frame with (see `initialParagraphsForDrag` in `use-text-drag-create`),
   * so identical text renders identically in an RP/IP frame and a text-tool frame (WMPCM-8162).
   *
   * Font family, font and size are always the book/checkpoint default, so a shape-turned-text-frame
   * and a text-tool-drag frame both start in the default font and size.
   */
  const newShapeTextSeedStyle = useMemo<NewShapeTextSeedStyle>(
    () => ({
      fontFamilyId: resolvedDefaultFontFamilyId,
      fontId: resolvedDefaultFontId,
      pointSize: defaultFontSize,
      kerning: newTextBoxKerning,
      tracking: newTextBoxTracking,
      underline: newTextBoxUnderline,
      alignment: newTextBoxAlignment,
    }),
    [
      newTextBoxKerning,
      newTextBoxTracking,
      newTextBoxUnderline,
      newTextBoxAlignment,
      defaultFontSize,
      resolvedDefaultFontFamilyId,
      resolvedDefaultFontId,
    ]
  )

  return { resolvedDefaultFontFamilyId, resolvedDefaultFontId, newShapeTextSeedStyle }
}
